import React, { useEffect, useState } from "react";
import { api, formatCurrency, formatDate } from "../lib/api";
import { useNavigate } from "react-router-dom";
import StatusBadge from "../components/app/StatusBadge";
import { ArrowUpRight, FileText, Users, CalendarBlank, TrendUp, ChartBar, Money, Receipt, CalendarDots, Hourglass, ChartLineUp, Repeat, Target, Coins } from "@phosphor-icons/react";

const STATUSES = ["bozza", "inviato", "accettato", "firmato", "rifiutato"];

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        setStats(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const monthly = stats?.monthly || {};
  const monthKeys = Object.keys(monthly).sort();
  const maxVal = Math.max(1, ...monthKeys.map((k) => monthly[k]));

  return (
    <div className="p-4 md:p-12 max-w-[1400px] mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-12">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Panoramica</div>
          <h1 className="font-display text-3xl sm:text-4xl md:text-5xl font-black tracking-tighter leading-none">Dashboard</h1>
        </div>
        <button
          onClick={() => navigate("/preventivi/nuovo")}
          data-testid="dashboard-new-quote-btn"
          className="btn-primary px-5 py-3 text-xs uppercase tracking-[0.15em] font-bold rounded-sm flex items-center gap-2 self-start"
        >
          Nuovo Preventivo <ArrowUpRight size={14} weight="bold" />
        </button>
      </div>

      {loading || !stats ? (
        <div className="text-[#64748B] text-sm">Caricamento…</div>
      ) : (
        <>
          {/* Top metric widgets */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
            <Metric label="Fatturato Firmato" value={formatCurrency(stats.revenue_signed)} icon={TrendUp} testid="metric-revenue" highlight />
            <Metric label="Pipeline (Inviati+Accettati)" value={formatCurrency(stats.revenue_pipeline)} icon={ChartBar} testid="metric-pipeline" />
            <Metric label="Clienti" value={stats.clients_count} icon={Users} testid="metric-clients" />
            <Metric label="Scadenze Aperte" value={stats.deadlines_open} icon={CalendarBlank} testid="metric-deadlines" />
          </div>

          {/* === PROSPETTIVE — proiezione finanziaria === */}
          <div className="mb-3 mt-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Prospettive</div>
            <h2 className="font-display text-xl sm:text-2xl font-black tracking-tight">Proiezione Finanziaria</h2>
            <p className="text-xs text-[#64748B] mt-1">Stima delle entrate considerando preventivi inviati, accettati e firmati non ancora saldati.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
            <Metric
              label="Da Incassare"
              value={formatCurrency(stats.pipeline_da_incassare || 0)}
              icon={Hourglass}
              testid="metric-da-incassare"
              accent="#FF9800"
              sublabel={`${stats.quotes_inviati_accettati_count || 0} aperti · ${formatCurrency(stats.pipeline_firmato_da_saldare || 0)} firmato`}
            />
            <Metric
              label="Entrate Previste"
              value={formatCurrency(stats.entrate_previste || 0)}
              icon={ChartLineUp}
              testid="metric-entrate-previste"
              accent="#0033A0"
              sublabel="Incassi reali + da incassare"
            />
            <Metric
              label="Balance Previsto"
              value={formatCurrency(stats.balance_previsto || 0)}
              icon={Target}
              testid="metric-balance-previsto"
              accent={(stats.balance_previsto || 0) >= 0 ? "#008A3C" : "#D32F2F"}
              sublabel="Entrate previste − Spese"
            />
            <Metric
              label="MRR Abbonamenti"
              value={formatCurrency(stats.mrr_attivo || 0)}
              icon={Repeat}
              testid="metric-mrr"
              accent="#7C3AED"
              sublabel="Ricavi mensili ricorrenti"
            />
          </div>

          {/* Financial metrics (CONSUNTIVO) */}
          <div className="mb-3 mt-2">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-2">Consuntivo</div>
            <h2 className="font-display text-xl sm:text-2xl font-black tracking-tight">Cassa Reale</h2>
            <p className="text-xs text-[#64748B] mt-1">Movimenti effettivi registrati: incassi confermati e spese sostenute.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6 mb-6">
            <Metric label="Totale Incassato" value={formatCurrency(stats.incassi_total || 0)} icon={Money} testid="metric-incassi" accent="#008A3C" />
            <Metric label="Totale Spese" value={formatCurrency(stats.spese_total || 0)} icon={Receipt} testid="metric-spese" accent="#D32F2F" />
            <Metric
              label="Bilancio"
              value={formatCurrency(stats.bilancio || 0)}
              icon={TrendUp}
              testid="metric-bilancio"
              accent={(stats.bilancio || 0) >= 0 ? "#008A3C" : "#D32F2F"}
            />
          </div>

          {/* Business KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6 mb-6">
            <Metric
              label="Ticket Medio Firmato"
              value={formatCurrency(stats.ticket_medio || 0)}
              icon={Coins}
              testid="metric-ticket-medio"
              sublabel={`${stats.quotes_firmati_count || 0} preventivi firmati`}
            />
            <Metric
              label="Tasso di Conversione"
              value={`${(stats.tasso_conversione ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 1 })}%`}
              icon={Target}
              testid="metric-tasso-conversione"
              sublabel="Firmati / Decisi"
            />
            <Metric
              label="Pipeline Totale"
              value={formatCurrency((stats.revenue_pipeline || 0) + (stats.pipeline_firmato_da_saldare || 0))}
              icon={ChartBar}
              testid="metric-pipeline-totale"
              sublabel="Tutti i lavori in corso"
            />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2 swiss-card p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Fatturato Firmato</div>
                  <h3 className="font-display text-xl font-bold mt-1">Andamento Mensile</h3>
                </div>
              </div>
              {monthKeys.length === 0 ? (
                <div className="text-xs text-[#64748B] py-10 text-center border border-dashed border-[#E2E8F0]">
                  Nessun preventivo firmato. Crea e fai firmare il primo preventivo per vedere i dati.
                </div>
              ) : (
                <div className="flex items-end gap-2 h-48">
                  {monthKeys.map((k) => (
                    <div key={k} className="flex-1 flex flex-col items-center gap-2">
                      <div className="text-[10px] font-mono text-[#0A0A0A]">{formatCurrency(monthly[k]).replace(/[€\s,]/g, "")}</div>
                      <div
                        className="w-full bg-[#0033A0] hover:bg-[#00267A] transition-all"
                        style={{ height: `${(monthly[k] / maxVal) * 80}%`, minHeight: 8 }}
                        title={`${k}: ${formatCurrency(monthly[k])}`}
                      />
                      <div className="text-[10px] uppercase tracking-[0.15em] text-[#64748B]">{k.replace("-", "/")}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="swiss-card p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Distribuzione</div>
              <h3 className="font-display text-xl font-bold mt-1 mb-6">Stato Preventivi</h3>
              <div className="space-y-3">
                {STATUSES.map((s) => {
                  const v = stats.by_status[s] || 0;
                  const total = stats.quotes_count || 1;
                  const pct = (v / total) * 100;
                  return (
                    <div key={s}>
                      <div className="flex items-center justify-between mb-1.5">
                        <StatusBadge stato={s} />
                        <span className="font-mono text-sm font-bold">{v}</span>
                      </div>
                      <div className="h-1 bg-[#F4F5F7] rounded-sm overflow-hidden">
                        <div className="h-full bg-[#0033A0]" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Recent quotes */}
          <div className="swiss-card p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B]">Attività Recente</div>
                <h3 className="font-display text-xl font-bold mt-1">Ultimi Preventivi</h3>
              </div>
              <button onClick={() => navigate("/preventivi")} className="text-xs uppercase tracking-[0.15em] font-bold text-[#0033A0] hover:underline">
                Vedi tutti
              </button>
            </div>
            {(!stats.recent || stats.recent.length === 0) ? (
              <div className="text-xs text-[#64748B] py-10 text-center border border-dashed border-[#E2E8F0]">
                Nessun preventivo. <button className="underline text-[#0033A0]" onClick={() => navigate("/preventivi/nuovo")}>Creane uno</button>.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E2E8F0] text-left">
                      <th className="py-2.5 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Numero</th>
                      <th className="py-2.5 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Cliente</th>
                      <th className="py-2.5 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Data</th>
                      <th className="py-2.5 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">Stato</th>
                      <th className="py-2.5 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold text-right">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent.map((q) => (
                      <tr key={q.id} className="border-b border-[#E2E8F0] hover:bg-[#F4F5F7] cursor-pointer" onClick={() => navigate(`/preventivi/${q.id}`)}>
                        <td className="py-3 font-mono text-xs font-bold">{q.numero}</td>
                        <td className="py-3">{q.cliente_snapshot?.ragione_sociale}</td>
                        <td className="py-3 text-[#64748B]">{formatDate(q.data_emissione)}</td>
                        <td className="py-3"><StatusBadge stato={q.stato} /></td>
                        <td className="py-3 text-right font-mono font-bold">{formatCurrency(q.totale)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function Metric({ label, value, icon: Icon, testid, highlight, accent, sublabel }) {
  const isHighlight = highlight;
  const accentColor = accent;
  return (
    <div
      className={`swiss-card p-3 sm:p-6 ${isHighlight ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : ""}`}
      style={accentColor && !isHighlight ? { borderLeft: `4px solid ${accentColor}` } : {}}
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-2">
        <div className={`text-[9px] sm:text-[10px] uppercase tracking-[0.2em] ${isHighlight ? "text-white/60" : "text-[#64748B]"}`}>{label}</div>
        <Icon size={16} weight="bold" className={isHighlight ? "text-white/80" : "text-[#0033A0]"} style={accentColor && !isHighlight ? { color: accentColor } : {}} />
      </div>
      <div className={`font-display text-xl sm:text-3xl font-black tracking-tighter mt-2 sm:mt-3 ${isHighlight ? "text-white" : ""}`} style={accentColor && !isHighlight ? { color: accentColor } : {}}>
        {value}
      </div>
      {sublabel && (
        <div className={`text-[10px] sm:text-[11px] mt-2 ${isHighlight ? "text-white/60" : "text-[#64748B]"} truncate`}>{sublabel}</div>
      )}
    </div>
  );
}
